import type { Dispatch, SetStateAction } from 'react';
import type { Delivery, IDeliveryLine, InventoryLevel, SiteLogistics, UntrackedMaterial } from '@interfaces/ILogistics';
import { LineStockAction } from './panels/LineStockAction';
import type { IStockIndex } from './stockActions';
import { buildAddStockDialog, buildRowClickStockDialog, buildSyncStockDialog, type IStockDialogState } from './logisticsStockDialog';
import { levelFor } from '@d-orca/shared/client/logistics';

interface IStockActionsArgs {
  data: SiteLogistics;
  stockIndex: IStockIndex;
  tz: string;
  canEdit: boolean;
  setStockDialog: Dispatch<SetStateAction<IStockDialogState | null>>;
}

/** The stock dialog's two openers and the delivery drawer's per-line action —
 *  one Sync and one Add stock for the Inventory rows and the drawer's material
 *  rows alike. See `buildAddStockDialog` for why the add prefill carries the
 *  delivery's own fields over. */
export function createStockActions({ data, stockIndex, tz, canEdit, setStockDialog }: IStockActionsArgs) {
  const syncStock = (level: InventoryLevel) => setStockDialog(buildSyncStockDialog(level, data.deliveries));
  const addStock = (material: UntrackedMaterial) => setStockDialog(buildAddStockDialog(material));
  // Resolved here, so the drawer never sees inventory.
  const lineAction = (delivery: Delivery, line: IDeliveryLine) => (
    <LineStockAction
      delivery={delivery}
      line={line}
      stockIndex={stockIndex}
      tz={tz}
      onSync={syncStock}
      onAddStock={addStock}
      canEdit={canEdit}
    />
  );
  /* "Count it in another unit" from an order's materials row: a material's
     unit is changed by recounting its stock in the new one, so this opens the
     material's own record (QA 2026-10-18). */
  const countInOtherUnit = (material: string) => {
    const level = levelFor(data.inventory, material);
    if (level) setStockDialog(buildRowClickStockDialog(level));
  };
  /** The same, from a surface that closes first (the order dialog, the
   *  delivery drawer) — the stock dialog opens in its place. */
  const countInOtherUnitFrom = (close: () => void) => (material: string) => {
    close();
    countInOtherUnit(material);
  };
  return { syncStock, addStock, lineAction, countInOtherUnit, countInOtherUnitFrom };
}
